// SPDX-License-Identifier: AGPL-3.0-or-later

import {CallMessage} from '@app/features/channel/components/CallMessage';
import {ChannelFollowAddMessage} from '@app/features/channel/components/ChannelFollowAddMessage';
import {ChannelIconChangeMessage} from '@app/features/channel/components/ChannelIconChangeMessage';
import {ChannelNameChangeMessage} from '@app/features/channel/components/ChannelNameChangeMessage';
import {GuildJoinMessage} from '@app/features/channel/components/GuildJoinMessage';
import {PinSystemMessage} from '@app/features/channel/components/PinSystemMessage';
import {RecipientAddMessage} from '@app/features/channel/components/RecipientAddMessage';
import {RecipientRemoveMessage} from '@app/features/channel/components/RecipientRemoveMessage';
import {UnknownMessage} from '@app/features/channel/components/UnknownMessage';
import {UserMessage} from '@app/features/channel/components/UserMessage';
import type {Channel} from '@app/features/channel/models/Channel';
import {ForumPostActions} from '@app/features/forum/components/ForumPostActions';
import {getPostForum} from '@app/features/forum/utils/ForumChannelUtils';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {ThreadCreatedMessage, ThreadStarterMessage} from '@app/features/threads/components/ThreadSystemMessages';
import ThreadGuilds from '@app/features/threads/state/ThreadGuilds';
import Users from '@app/features/user/state/Users';
import {MessageTypes} from '@fluxer/constants/src/ChannelConstants';
import type React from 'react';

export function getMessageComponent(
	message: Message,
	_channel: Channel,
	forceUnknownMessageType = false,
): React.ReactElement {
	const currentUser = Users.getCurrentUser();
	if (forceUnknownMessageType && currentUser && message.author.id === currentUser.id) {
		return <UnknownMessage data-flx="messaging.message-component-utils.get-message-component.unknown-message" />;
	}
	switch (message.type) {
		case MessageTypes.USER_JOIN:
			return (
				<GuildJoinMessage
					message={message}
					data-flx="messaging.message-component-utils.get-message-component.guild-join-message"
				/>
			);
		case MessageTypes.CHANNEL_PINNED_MESSAGE:
			return (
				<PinSystemMessage
					message={message}
					data-flx="messaging.message-component-utils.get-message-component.pin-system-message"
				/>
			);
		case MessageTypes.RECIPIENT_ADD:
			return (
				<RecipientAddMessage
					message={message}
					data-flx="messaging.message-component-utils.get-message-component.recipient-add-message"
				/>
			);
		case MessageTypes.RECIPIENT_REMOVE:
			return (
				<RecipientRemoveMessage
					message={message}
					data-flx="messaging.message-component-utils.get-message-component.recipient-remove-message"
				/>
			);
		case MessageTypes.CALL:
			return (
				<CallMessage
					message={message}
					data-flx="messaging.message-component-utils.get-message-component.call-message"
				/>
			);
		case MessageTypes.CHANNEL_NAME_CHANGE:
			return (
				<ChannelNameChangeMessage
					message={message}
					data-flx="messaging.message-component-utils.get-message-component.channel-name-change-message"
				/>
			);
		case MessageTypes.CHANNEL_ICON_CHANGE:
			return (
				<ChannelIconChangeMessage
					message={message}
					data-flx="messaging.message-component-utils.get-message-component.channel-icon-change-message"
				/>
			);
		case MessageTypes.CHANNEL_FOLLOW_ADD:
			return (
				<ChannelFollowAddMessage
					message={message}
					data-flx="messaging.message-component-utils.get-message-component.channel-follow-add-message"
				/>
			);
		case MessageTypes.THREAD_CREATED:
			return ThreadGuilds.isActive(message.guildId ?? _channel.guildId) ? (
				<ThreadCreatedMessage
					message={message}
					data-flx="messaging.message-component-utils.get-message-component.thread-created-message"
				/>
			) : (
				<UnknownMessage data-flx="messaging.message-component-utils.get-message-component.unknown-message--3" />
			);
		case MessageTypes.THREAD_STARTER_MESSAGE:
			return ThreadGuilds.isActive(message.guildId ?? _channel.guildId) ? (
				<ThreadStarterMessage
					message={message}
					data-flx="messaging.message-component-utils.get-message-component.thread-starter-message"
				/>
			) : (
				<UnknownMessage data-flx="messaging.message-component-utils.get-message-component.unknown-message--4" />
			);
		case MessageTypes.DEFAULT:
		case MessageTypes.REPLY:
		case MessageTypes.CLIENT_SYSTEM:
		case MessageTypes.CHAT_INPUT_COMMAND:
			if (message.id === _channel.id && getPostForum(_channel)) {
				return (
					<>
						<UserMessage data-flx="messaging.message-component-utils.get-message-component.user-message--post" />
						<ForumPostActions
							post={_channel}
							messageId={message.id}
							data-flx="messaging.message-component-utils.get-message-component.forum-post-actions"
						/>
					</>
				);
			}
			return <UserMessage data-flx="messaging.message-component-utils.get-message-component.user-message" />;
		default:
			return <UnknownMessage data-flx="messaging.message-component-utils.get-message-component.unknown-message--2" />;
	}
}
